import {
    EnforcementLogItem,
    EnforcementLogResponse,
    useEnforcementLogOutlet,
} from './EnforcementLogList.tsx';
import { Navigate, useNavigate, useParams } from 'react-router';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { Checklist } from '@mui/icons-material';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { EnforcementResult } from 'enforcement-result/src/EnforcementResult.tsx';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { EnforcedLicensePlate } from 'enforcement-result/src/EnforcementTypes.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { makeSearchQueryWithFilters } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { Language } from 'dg-web-shared/lib/Localized.ts';

export function EnforcementLogDetail() {
    const urlParams = useParams<{ enforcementLogId: string }>();
    const enforcementLogId = urlParams.enforcementLogId
        ? parseInt(urlParams.enforcementLogId, 10)
        : null;
    const { enforcementLogList, searchText, activeFilters } =
        useEnforcementLogOutlet();
    const navigate = useNavigate();

    const enforcementResult = enforcementLogList.find(
        list => list.enforcementLogId === enforcementLogId,
    );
    const backUrl = `..${makeSearchQueryWithFilters(
        searchText,
        activeFilters,
    )}`;

    if (!enforcementResult) {
        return <Navigate to={backUrl} />;
    }
    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={backUrl}
            render={() => (
                <OperatorRoutedModalContent
                    header={
                        <HeaderComponent
                            Icon={Checklist}
                            title={enforcementResult.identification}
                            editMode={false}
                            buttons={
                                <HeaderActionCloseButtons
                                    onClose={() => navigate(backUrl)}
                                />
                            }
                        />
                    }
                    body={
                        <EnforcementLogDetailBody
                            enforcementResult={enforcementResult}
                        />
                    }
                />
            )}
        />
    );
}

function EnforcementLogDetailBody({
    enforcementResult,
}: {
    enforcementResult: EnforcementLogItem;
}) {
    const { language } = useLanguage();
    const [enforcmentResultState] = useServerFetch<
        { enforcementResponse: EnforcedLicensePlate[] | null },
        { enforcementLogId: number }
    >(
        ({ enforcementLogId }) => ({
            url: `/ui-api/operator-account/enforcement-log/enforcement-response/${enforcementLogId}`,
        }),
        { enforcementLogId: enforcementResult.enforcementLogId },
    );
    return (
        <FormContentLayout>
            <FormLeftColumn>
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Referenz"
                            fr="Référence"
                            it="Referenza"
                            en="Reference"
                        />
                    }
                    value={enforcementResult.enforcementLogId}
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Kennzeichen"
                            fr="Immatriculation"
                            it="Targa"
                            en="License plate"
                        />
                    }
                    value={enforcementResult.identification}
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Ort"
                            fr="Localité"
                            it="Località"
                            en="Place"
                        />
                    }
                    value={enforcementResult.locationName}
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Eingabe"
                            fr="Saisie"
                            it="Inserimento"
                            en="Input"
                        />
                    }
                    value={`${enforcementResult.rawIdentification} (${deriveRecognitionType(
                        enforcementResult.recognitionType,
                        language,
                    )})`}
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Kontrolleur"
                            fr="Contrôleur"
                            it="Controllore"
                            en="Controller"
                        />
                    }
                    value={enforcementResult.enforcerName}
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Gerätetyp"
                            fr="Type d'appareil"
                            it="Tipo d'apparecchio"
                            en="Device type"
                        />
                    }
                    value={enforcementResult.clientName}
                />
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Resultat"
                            fr="Résultat"
                            it="Risultato"
                            en="Resultat"
                        />
                    }
                    sx={{ textTransform: 'uppercase' }}
                    value={
                        <EnforcementLogResponse
                            result={enforcementResult.responseResult}
                        />
                    }
                />
            </FormLeftColumn>
            <FormRightColumn>
                {enforcmentResultState.status === RequestStatus.SUCCESS && (
                    <EnforcementResult
                        result={enforcmentResultState.data.enforcementResponse}
                    />
                )}
            </FormRightColumn>
        </FormContentLayout>
    );
}

export const deriveRecognitionType = (
    type: string,
    language: Language,
): string => {
    const rfid = {
        de: 'Vignette-Scan',
        fr: 'Scansion de la vignette',
        it: 'Scansione vignetta',
        en: 'Vignette scan',
    };
    const lpr = {
        de: 'Nummernschilderkennung',
        fr: 'Reconnaissance de la plaque',
        it: 'Riconoscimento targa',
        en: 'License plate recognition',
    };
    const keyboard = {
        de: 'Tastatur',
        fr: 'Manuelle',
        it: 'Manuale',
        en: 'Keyboard',
    };
    switch (type) {
        case 'rfid':
            return rfid[language];
        case 'lpr':
            return lpr[language];
        case 'keyboard':
            return keyboard[language];
        default:
            console.error(`Enforcement recognition type '${type}' is unknown.`);
            return keyboard[language];
    }
};
